import { Link } from 'react-router';
import { ArrowLeft, Heart, Award, Globe } from 'lucide-react';
import { PublicFooter } from '@/components/PublicFooter';
import { usePageMeta } from '@/hooks/usePageMeta';
import { JsonLd } from '@/components/seo/JsonLd';
import { buildOrganizationSchema } from '@/components/seo/schemas';

const values = [
  {
    icon: Award,
    title: 'Reseller-First Design',
    description:
      "Every feature starts with a real reseller workflow. We don't build for hypotheticals — we talk to our users weekly and ship what actually moves their business forward.",
  },
  {
    icon: Globe,
    title: 'Open Ecosystem',
    description:
      'Your data belongs to you. Full export, no lock-in, and we integrate with the platforms you already use rather than forcing you into a walled garden.',
  },
  {
    icon: Heart,
    title: '1% for the Planet',
    description:
      'We pledge 1% of our annual revenue to environmental nonprofits focused on waste reduction and circular economy initiatives. The reselling industry is inherently sustainable — we want to amplify that impact.',
  },
];

export default function About() {
  usePageMeta({
    title: 'About For Visibility — AI-Native Reseller Operations',
    description:
      'For Visibility is an AI-native operating system for professional resellers — unifying inventory, AI listing generation, sold-comp pricing, and autonomous agents. Built reseller-first.',
    canonicalPath: '/about',
    ogPage: 'about',
  });
  return (
    <div className="min-h-screen bg-fos-bg-base">
      <JsonLd data={buildOrganizationSchema()} />
      <div className="mx-auto max-w-3xl px-4 py-12">
        <Link
          to="/"
          className="mb-8 inline-flex items-center gap-1 text-sm text-fos-text-muted transition-colors hover:text-fos-text-primary"
        >
          <ArrowLeft className="h-4 w-4" /> Back to home
        </Link>

        <article className="prose prose-gray max-w-none">
          <h1>About For Visibility</h1>

          <p className="lead">
            We built For Visibility because we lived the problem. After years of juggling eBay,
            Mercari, and Poshmark across browser tabs, spreadsheets, and sticky notes, we decided
            there had to be a better way.
          </p>

          <h2>Our Origin</h2>
          <p>
            For Visibility started in 2024 as a side project by a small team of resellers and
            engineers who were tired of duct-taping together separate tools to run their businesses.
            The first version was a simple dashboard that connected eBay listings to a local
            spreadsheet — and it immediately saved hours each week.
          </p>
          <p>
            That early prototype became the foundation for what For Visibility is today: an
            AI-native operating system purpose-built for resellers. We officially launched in early
            2025 and have been growing with our community ever since.
          </p>

          <h2>Our Values</h2>
        </article>

        <div className="mt-6 grid gap-6 sm:grid-cols-3">
          {values.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.title}
                className="rounded-lg border border-fos-border bg-fos-bg-card p-6"
              >
                <Icon className="mb-3 h-6 w-6 text-fos-accent" />
                <h3 className="mb-2 text-sm font-semibold text-fos-text-primary">{item.title}</h3>
                <p className="text-sm leading-relaxed text-fos-text-muted">{item.description}</p>
              </div>
            );
          })}
        </div>

        <article className="prose prose-gray mt-12 max-w-none">
          <h2>Our 1% Pledge</h2>
          <p>
            For Visibility is a proud member of <strong>1% for the Planet</strong>. Each year, we
            contribute 1% of our annual revenue to environmental nonprofits focused on waste
            reduction and the circular economy. We believe the reselling industry is a natural ally
            in the fight against fast-fashion waste and consumer overproduction, and we're committed
            to putting our resources behind organizations doing the work.
          </p>

          <h2>Join Our Community</h2>
          <p>
            For Visibility is built in the open, with input from our users shaping every major
            feature. Whether you're a casual seller running a side hustle or a full-time reseller
            managing multiple shops, we'd love to have you.{' '}
            <Link to="/pricing" className="text-fos-accent hover:underline">
              Start free
            </Link>{' '}
            or{' '}
            <Link to="/help" className="text-fos-accent hover:underline">
              learn more
            </Link>
            .
          </p>
        </article>
      </div>
      <PublicFooter />
    </div>
  );
}
